import { Box, ListItem, Typography, Link as MuiLink } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router';
import { KeyofNamespace } from '../../../../../../translations/keyofNs';
import { Chip } from '../../../../atomic';

const linkStyles = {
  color: 'unset',
  textDecoration: 'none',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center'
};

type Props = {
  text: KeyofNamespace<'common'>;
  route: string;
  activeRoutes?: string[];
  isExternal?: boolean;
  isBeta?: boolean;
  /** Something below this area needs the company to act. */
  hasTodo?: boolean;
};

export type MenuItem = Props;

export const NavItem: FC<Props> = ({
  text,
  route,
  activeRoutes,
  isExternal = false,
  isBeta = false,
  hasTodo = false
}) => {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();

  const Link = isExternal ? styled(MuiLink)(linkStyles) : styled(RouterLink)(linkStyles);

  const to = !isExternal ? `/${route}` : '';
  const href = isExternal ? route : '';

  activeRoutes = activeRoutes ? [route, ...activeRoutes] : [route];
  const isActive = activeRoutes.some((route) => pathname.startsWith(`/${route}`));

  return (
    <ListItem
      key={text}
      sx={(theme) => ({
        paddingX: 0,
        py: '5px',
        marginX: theme.spacing(3),
        ...(isActive && {
          pb: 0,
          borderBottom: `5px solid ${theme.palette.heaven.main}`
        }),
        '&:hover': {
          borderBottomColor: theme.palette.grey[500],
          '& p': {
            color: theme.palette.grey[500]
          }
        }
      })}
    >
      <Link to={to} href={href}>
        <Box>
          <Typography
            className="flex flex-row gap-x-2"
            variant="body1"
            sx={(theme) => ({
              ...(isActive && {
                color: theme.palette.heaven.main,
                fontWeight: '500'
              })
            })}
          >
            {t(text)}
            {hasTodo && (
              <Box
                component="span"
                aria-hidden
                className="h-2 w-2 self-center shrink-0 rounded-full bg-error-main"
              />
            )}
            {isBeta && <Chip label={t('common:beta')} color="heaven" />}
          </Typography>
        </Box>
      </Link>
    </ListItem>
  );
};
